import { LitElement, html, css } from "lit";
import { customElement, state, query } from "lit/decorators.js";

import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/button/text-button.js";
import "@material/web/dialog/dialog.js";
import "@material/web/icon/icon.js";
import "@material/web/progress/circular-progress.js";
import type { MdDialog } from "@material/web/dialog/dialog.js";

import "../components/app-shell";
import { sharedStyles } from "../styles/shared";
import { icon } from "../lib/icons";
import { inputValue, errorMessage } from "../lib/dom";
import { toast } from "../lib/toast";
import { AuthController } from "../lib/auth-controller";
import { api, ApiError } from "../lib/api";

// GiAuthority as the server marshals it: the Go struct has no json tags, so
// the JSON keys are the Go field names verbatim (see
// internal/modules/giregistry/trust_domain.go).
interface GiAuthority {
  ID: string;
  Name: string;
  DID: string;
  Role: string; // RTAO | TAO
  Country: string;
  Jurisdiction: string; // eu | country | ""
  Sector: string; // gi | high_value | ""
  Categories: string[] | null;
  ParentID: string;
  TenantOrgID: string; // "" = not a tenant
  Status: string; // active | revoked
  CreatedAt: string;
  UpdatedAt: string;
}

// GiAccreditation (same no-json-tags rule) — only the fields this page renders.
interface GiAccreditation {
  ID: string;
  Type: string;
  AccreditorID: string;
  AccreditorName: string;
  SubjectKind: string; // authority | producer
  SubjectID: string;
  Status: string; // active | revoked
}

interface AuthorityForm {
  name: string;
  did: string;
  role: string;
  country: string;
  jurisdiction: string;
  sector: string;
  categories: string;
  parentId: string;
}

const EMPTY_FORM: AuthorityForm = {
  name: "",
  did: "",
  role: "TAO",
  country: "",
  jurisdiction: "",
  sector: "",
  categories: "",
  parentId: "",
};

// The GI trust-authority hierarchy (EBSI RTAO/TAO model) + the consortium
// billing link. The tree runs EU/framework → national authority (e.g. INAO,
// jurisdiction "country") → sector consortium (e.g. Comité Champagne, sector
// "gi", categories ["wine"]); "high_value" is the sibling sector for non-GI
// brand protection. Linking an authority to a tenancy org is what makes a
// consortium a payable customer — everything here is operator-only server-side.
@customElement("authorities-page")
export class AuthoritiesPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .indent {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .tree-elbow {
        color: var(--md-sys-color-outline);
        font-family: var(--font-mono, ui-monospace, monospace);
      }
      .register-panel {
        background: color-mix(in srgb, var(--md-sys-color-primary) 5%, transparent);
      }
      .register-panel h2 {
        font-size: 1rem;
      }
      .cats {
        display: inline-flex;
        gap: 4px;
        flex-wrap: wrap;
      }
      .cat {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 500;
        background: var(--md-sys-color-surface-container-highest);
        color: var(--md-sys-color-on-surface-variant);
      }
      .edge {
        display: block;
        color: var(--md-sys-color-on-surface-variant);
        font-size: 12px;
      }
      .edge--revoked {
        text-decoration: line-through;
      }
      form.dialog-form {
        display: grid;
        gap: 16px;
      }
      .forbidden {
        display: flex;
        align-items: center;
        gap: 10px;
      }
    `,
  ];

  @state() private loading = true;
  @state() private error = "";
  @state() private forbidden = false;
  @state() private authorities: GiAuthority[] = [];
  @state() private accreditations: GiAccreditation[] = [];
  @state() private registerOpen = false;
  @state() private saving = false;
  @state() private formError = "";
  @state() private form: AuthorityForm = { ...EMPTY_FORM };
  @state() private linkTarget: GiAuthority | null = null;
  @state() private linkOrgId = "";
  @state() private linking = false;
  @state() private linkError = "";

  @query("#billing-dialog") private billingDialog?: MdDialog;

  private auth = new AuthController(this);

  // Operator-only affordances are hidden from other users (the server enforces
  // it too — no fake buttons).
  private get canManage(): boolean {
    return this.auth.hasRole("platform_operator");
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    this.forbidden = false;
    try {
      this.authorities = (
        await api.get<{ authorities: GiAuthority[] | null }>("/gi/authorities")
      ).authorities ?? [];
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 401)) {
        this.forbidden = true;
      } else {
        this.error = errorMessage(err, "Failed to load authorities.");
      }
      this.loading = false;
      return;
    }
    // Accreditation edges are decoration on the tree — best effort, never
    // sinks the page.
    try {
      this.accreditations = (
        await api.get<{ accreditations: GiAccreditation[] | null }>("/gi/accreditations")
      ).accreditations ?? [];
    } catch {
      this.accreditations = [];
    }
    this.loading = false;
  }

  /** Authorities as an indented tree: roots first, children under their
   * parent. An orphan (parent id not in the loaded set) renders as a root so
   * nothing silently disappears; a seen-set guards against cycles. */
  private flattenTree(): Array<{ authority: GiAuthority; depth: number }> {
    const ids = new Set(this.authorities.map((a) => a.ID));
    const children = new Map<string, GiAuthority[]>();
    for (const a of this.authorities) {
      const key = a.ParentID && ids.has(a.ParentID) ? a.ParentID : "";
      const list = children.get(key) ?? [];
      list.push(a);
      children.set(key, list);
    }
    for (const list of children.values()) {
      list.sort((x, y) => x.Name.localeCompare(y.Name));
    }
    const out: Array<{ authority: GiAuthority; depth: number }> = [];
    const seen = new Set<string>();
    const walk = (parentId: string, depth: number): void => {
      for (const a of children.get(parentId) ?? []) {
        if (seen.has(a.ID)) continue;
        seen.add(a.ID);
        out.push({ authority: a, depth });
        walk(a.ID, depth + 1);
      }
    };
    walk("", 0);
    return out;
  }

  /** Accreditation edges pointing at an authority (who accredited it). */
  private edgesInto(authorityId: string): GiAccreditation[] {
    return this.accreditations.filter(
      (e) => e.SubjectKind === "authority" && e.SubjectID === authorityId,
    );
  }

  private async register(e: Event): Promise<void> {
    e.preventDefault();
    const name = this.form.name.trim();
    if (!name) {
      this.formError = "Name is required.";
      return;
    }
    this.saving = true;
    this.formError = "";
    try {
      const created = await api.post<GiAuthority>("/gi/authorities", {
        name,
        did: this.form.did.trim(),
        role: this.form.role,
        country: this.form.country.trim(),
        jurisdiction: this.form.jurisdiction,
        sector: this.form.sector,
        categories: this.form.categories
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
        parentId: this.form.parentId,
      });
      this.authorities = [...this.authorities, created];
      this.form = { ...EMPTY_FORM };
      this.registerOpen = false;
      toast(`Registered ${created.Name} (${created.Role})`);
    } catch (err) {
      this.formError = errorMessage(err, "Could not register the authority.");
    } finally {
      this.saving = false;
    }
  }

  private openBillingDialog(a: GiAuthority): void {
    this.linkTarget = a;
    this.linkOrgId = a.TenantOrgID;
    this.linkError = "";
    this.billingDialog?.show();
  }

  private async saveBillingLink(e: Event): Promise<void> {
    e.preventDefault();
    const target = this.linkTarget;
    if (!target) return;
    const orgId = this.linkOrgId.trim();
    this.linking = true;
    this.linkError = "";
    try {
      const updated = await api.put<GiAuthority>(
        `/gi/authorities/${target.ID}/tenant`,
        { orgId },
      );
      this.authorities = this.authorities.map((a) =>
        a.ID === updated.ID ? updated : a,
      );
      this.billingDialog?.close();
      toast(
        orgId
          ? `${updated.Name} now bills through org ${orgId}`
          : `${updated.Name} unlinked — no longer a tenant`,
      );
    } catch (err) {
      this.linkError = errorMessage(err, "Could not update the billing link.");
    } finally {
      this.linking = false;
    }
  }

  render() {
    return html`
      <app-shell active="/authorities">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Trust authorities</h1>
              <p>
                The GI accreditation hierarchy — EU framework → national
                authority (RTAO, e.g. INAO) → sector consortium (TAO, e.g.
                Comité Champagne) — and the tenancy org each consortium bills
                through.
              </p>
            </div>
            <div class="actions-row" ?hidden=${!this.canManage || this.forbidden}>
              <md-filled-button
                @click=${() => (this.registerOpen = !this.registerOpen)}
              >
                ${icon("add")} Register authority
              </md-filled-button>
            </div>
          </div>

          ${this.registerOpen && this.canManage ? this.renderRegisterForm() : null}

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.forbidden
              ? html`<div class="card forbidden">
                  ${icon("shield")}
                  <div>
                    <b>Operator access required.</b>
                    <div class="muted">
                      The trust-authority registry is restricted to platform
                      operators — your account doesn't carry that role.
                    </div>
                  </div>
                </div>`
              : this.error
                ? html`<p class="error-text">${this.error}</p>`
                : this.renderTree()}
        </div>

        ${this.renderBillingDialog()}
      </app-shell>
    `;
  }

  private renderRegisterForm() {
    const activeParents = this.authorities.filter((a) => a.Status === "active");
    return html`
      <div class="card register-panel">
        <h2>Register an authority</h2>
        <form class="form-grid" @submit=${(e: Event) => void this.register(e)}>
          <div class="form-row">
            <md-outlined-text-field
              label="Name"
              required
              .value=${this.form.name}
              @input=${(e: Event) => (this.form = { ...this.form, name: inputValue(e) })}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="DID (optional)"
              supporting-text="did:ebsi:… placeholder until EBSI onboarding"
              .value=${this.form.did}
              @input=${(e: Event) => (this.form = { ...this.form, did: inputValue(e) })}
            ></md-outlined-text-field>
          </div>
          <div class="form-row">
            <md-outlined-select
              label="Role"
              .value=${this.form.role}
              @change=${(e: Event) => (this.form = { ...this.form, role: inputValue(e) })}
            >
              <md-select-option value="RTAO">
                <div slot="headline">RTAO — root authority (e.g. INAO)</div>
              </md-select-option>
              <md-select-option value="TAO">
                <div slot="headline">TAO — consortium (e.g. Comité Champagne)</div>
              </md-select-option>
            </md-outlined-select>
            <md-outlined-text-field
              label="Country"
              .value=${this.form.country}
              @input=${(e: Event) => (this.form = { ...this.form, country: inputValue(e) })}
            ></md-outlined-text-field>
          </div>
          <div class="form-row">
            <md-outlined-select
              label="Jurisdiction"
              .value=${this.form.jurisdiction}
              @change=${(e: Event) =>
                (this.form = { ...this.form, jurisdiction: inputValue(e) })}
            >
              <md-select-option value="">
                <div slot="headline">— none —</div>
              </md-select-option>
              <md-select-option value="eu">
                <div slot="headline">eu — EU / framework level</div>
              </md-select-option>
              <md-select-option value="country">
                <div slot="headline">country — national authority</div>
              </md-select-option>
            </md-outlined-select>
            <md-outlined-select
              label="Sector"
              .value=${this.form.sector}
              @change=${(e: Event) => (this.form = { ...this.form, sector: inputValue(e) })}
            >
              <md-select-option value="">
                <div slot="headline">— none —</div>
              </md-select-option>
              <md-select-option value="gi">
                <div slot="headline">gi — GI consortium</div>
              </md-select-option>
              <md-select-option value="high_value">
                <div slot="headline">high_value — brand protection</div>
              </md-select-option>
            </md-outlined-select>
          </div>
          <div class="form-row">
            <md-outlined-text-field
              label="Categories"
              supporting-text="Comma-separated, e.g. wine, spirits, watches"
              .value=${this.form.categories}
              @input=${(e: Event) =>
                (this.form = { ...this.form, categories: inputValue(e) })}
            ></md-outlined-text-field>
            <md-outlined-select
              label="Parent authority"
              supporting-text="Who accredits this authority"
              .value=${this.form.parentId}
              @change=${(e: Event) =>
                (this.form = { ...this.form, parentId: inputValue(e) })}
            >
              <md-select-option value="">
                <div slot="headline">— top of the tree —</div>
              </md-select-option>
              ${activeParents.map(
                (a) => html`
                  <md-select-option value=${a.ID}>
                    <div slot="headline">${a.Name} (${a.Role})</div>
                  </md-select-option>
                `,
              )}
            </md-outlined-select>
          </div>
          ${this.formError
            ? html`<p class="error-text">${this.formError}</p>`
            : null}
          <div class="actions-row">
            <md-filled-button type="submit" ?disabled=${this.saving}>
              ${this.saving ? "Registering…" : "Register"}
            </md-filled-button>
            <md-text-button
              type="button"
              @click=${() => {
                this.registerOpen = false;
                this.formError = "";
              }}
            >
              Cancel
            </md-text-button>
          </div>
        </form>
      </div>
    `;
  }

  private renderTree() {
    const rows = this.flattenTree();
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Hierarchy <small>· ${this.authorities.length} authorities</small></h2>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Authority</th>
              <th>Role</th>
              <th>Country</th>
              <th>Jurisdiction</th>
              <th>Sector</th>
              <th>Categories</th>
              <th>Status</th>
              <th>Billing</th>
              ${this.canManage ? html`<th></th>` : null}
            </tr>
          </thead>
          <tbody>
            ${rows.length === 0
              ? html`<tr>
                  <td colspan=${this.canManage ? 9 : 8} class="empty">
                    No authorities registered yet. Start at the top: register
                    the framework/EU root, then the national authority, then
                    each consortium under it.
                  </td>
                </tr>`
              : rows.map(({ authority, depth }) => this.renderRow(authority, depth))}
          </tbody>
        </table></div>
      </div>
    `;
  }

  private renderRow(a: GiAuthority, depth: number) {
    const edges = this.edgesInto(a.ID);
    return html`
      <tr>
        <td>
          <span class="indent" style=${`padding-left:${depth * 22}px`}>
            ${depth > 0 ? html`<span class="tree-elbow">└</span>` : null}
            <span>
              <b>${a.Name}</b>
              ${a.DID ? html`<small class="mono">${a.DID}</small>` : null}
              ${edges.map(
                (e) => html`<small
                  class="edge ${e.Status === "active" ? "" : "edge--revoked"}"
                  title=${e.Type}
                  >accredited by ${e.AccreditorName || "framework root"}</small
                >`,
              )}
            </span>
          </span>
        </td>
        <td>
          <span class="badge ${a.Role === "RTAO" ? "badge--gi" : "badge--neutral"}">
            ${icon(a.Role === "RTAO" ? "account_balance" : "groups")} ${a.Role}
          </span>
        </td>
        <td>${a.Country || html`<span class="muted">—</span>`}</td>
        <td>${a.Jurisdiction || html`<span class="muted">—</span>`}</td>
        <td>${a.Sector || html`<span class="muted">—</span>`}</td>
        <td>
          ${a.Categories?.length
            ? html`<span class="cats"
                >${a.Categories.map((c) => html`<span class="cat">${c}</span>`)}</span
              >`
            : html`<span class="muted">—</span>`}
        </td>
        <td>
          <span class="badge ${a.Status === "active" ? "badge--ok" : "badge--bad"}">
            ${icon(a.Status === "active" ? "check_circle" : "gpp_bad")} ${a.Status}
          </span>
        </td>
        <td>
          ${a.TenantOrgID
            ? html`<span class="badge badge--ok" title="Bills through this tenancy org">
                ${icon("link")} <code>${a.TenantOrgID}</code>
              </span>`
            : html`<span class="muted">not a tenant</span>`}
        </td>
        ${this.canManage
          ? html`<td>
              <md-text-button @click=${() => this.openBillingDialog(a)}>
                <span slot="icon">${icon(a.TenantOrgID ? "edit" : "link")}</span>
                Billing link
              </md-text-button>
            </td>`
          : null}
      </tr>
    `;
  }

  private renderBillingDialog() {
    const target = this.linkTarget;
    return html`
      <md-dialog id="billing-dialog">
        <div slot="headline">
          ${target ? `Billing link — ${target.Name}` : "Billing link"}
        </div>
        <form
          slot="content"
          id="billing-form"
          class="dialog-form"
          @submit=${(e: Event) => void this.saveBillingLink(e)}
        >
          <p class="muted" style="margin:0">
            Linking a tenancy org is what makes this authority a payable
            customer — the consortium bills through that org.
          </p>
          <md-outlined-text-field
            label="Organization id"
            supporting-text="The tenancy organization id; empty unlinks"
            .value=${this.linkOrgId}
            @input=${(e: Event) => (this.linkOrgId = inputValue(e))}
          ></md-outlined-text-field>
          ${this.linkError
            ? html`<p class="error-text">${this.linkError}</p>`
            : null}
        </form>
        <div slot="actions">
          <md-text-button @click=${() => this.billingDialog?.close()}>
            Cancel
          </md-text-button>
          <md-filled-button form="billing-form" type="submit" ?disabled=${this.linking}>
            ${this.linking
              ? "Saving…"
              : this.linkOrgId.trim()
                ? "Link org"
                : "Unlink"}
          </md-filled-button>
        </div>
      </md-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "authorities-page": AuthoritiesPage;
  }
}
